import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { ContactCreateDTO, ContactDTO, ContactUpdateDTO } from '@/types';
import { useGlobalCompany } from './useGlobalCompany';

interface UseContactsOptions {
  search?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

export function useContacts(options: UseContactsOptions = {}) {
  const { search, page = 1, pageSize = 25, enabled = true } = options;
  const { selectedCompanyId } = useGlobalCompany();

  return useQuery({
    queryKey: ['contacts', { search, page, pageSize, companyId: selectedCompanyId }],
    queryFn: async (): Promise<{ data: ContactDTO[]; total?: number; page?: number; pageSize?: number }> => {
      const response = await apiService.getContacts({
        search,
        page,
        pageSize,
      });

      // Support both array and paginated response shapes
      if (Array.isArray(response)) {
        return { data: response, total: response.length, page, pageSize };
      }

      const { items, contacts, results, data, total, total_count, page: responsePage, pageSize: responsePageSize } = response;
      const resolvedContacts = items ?? contacts ?? results ?? data ?? [];
      return {
        data: resolvedContacts,
        total: total ?? total_count ?? resolvedContacts.length,
        page: responsePage ?? page,
        pageSize: responsePageSize ?? pageSize,
      };
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useContact(contactId?: string, enabled = true) {
  return useQuery({
    queryKey: ['contact', contactId],
    queryFn: async (): Promise<ContactDTO> => apiService.getContactById(contactId!),
    enabled: !!contactId && enabled,
    staleTime: 60_000,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ContactCreateDTO) => apiService.createContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContact(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ContactUpdateDTO) => apiService.updateContact(contactId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
    },
  });
}

